import { createSlice } from '@reduxjs/toolkit';
import { formatAtsCandidate } from '../common/formatAtsCandidate';
import {
  fetchActivityDrillDown,
  fetchAtsHiresPerformance,
  fetchAtsHiresPerformanceDrillDown,
  fetchAtsOfferPerformance,
  fetchAtsOfferPerformanceDrillDown,
  fetchDei,
  fetchFlowActivity,
  fetchFlowPerformance,
  fetchFlowPerformanceDrillDown,
  fetchRecruiterActivity,
  fetchRepliedCandidatesByRecruiter,
  fetchRepliedCandidatesByRecruiterDrillDown,
} from './actions';
import groupBy from 'lodash.groupby'

/**
 * Table is "grouped by" in this order
 * - g1 - ["added", "replied", "interested"]
 * - g2 - ["male", "female", "unknown"]
 *
 * Each grouped data set's value is a nested array
 * at index [0]. The actual data resides at the second
 * level in an array whose index is determined by
 * the "header".
 */
const formatDeiData = (payload) => {
  const { table = [], lookups = {}, headers = [] } = payload;

  const obj = table.reduce((g1Acc, g1, index) => {
    const g1Key = lookups?.['g1']?.[index];
    if (g1Key) {
      g1Acc[g1Key] = g1[0].reduce((g2Acc, g2, index) => {
        const g2Key = lookups?.['g2']?.[index];
        if (g2Key) {
          const valueIndex = headers.indexOf(g1Key);
          // "header" will have list of columns
          // these need to be mapped to to the lookup key
          // and data to be retrieved from that.
          // Example - if "g1Key" is "added", value for that
          // will reside in index of "added" in "headers".
          g2Acc[g2Key] = g2?.[0]?.map((data) => data[valueIndex]);
        }
        return g2Acc;
      }, {});
    }
    return g1Acc;
  }, {});

  obj.xAxis = lookups.dt ?? [];
  return obj;
};

const constructTabularData = (payload) => {
  const { table = [], headers = [], lookups = {} } = payload;

  return table.reduce((acc, data) => {
    const obj = {};
    headers.forEach((column, inx) => {
      const value = data[inx];
      // Lookups may or may not be unique
      // so it's safe to use the actual value
      // provided in the field
      if (lookups[column]?.[value]) {
        obj[column] = lookups[column]?.[value];
      } else {
        obj[column] = value;
      }
    });

    acc.push(obj);
    return acc;
  }, []);
};

const constructComparativeTabularData = (payload) => {
  const { table = [], headers = [], lookups = {} } = payload;

  return Object.values(groupBy(table, row => row[2]))
		.map(([row1, row2]) => {
			if (row1?.[0] === '2' || row2?.[0] === '3') {
				return [row1, row2]
			}
			return [row2, row1]
		})
		.reduce(
			(acc, [row1 = {}, row2 = {}], rowInx) => {
				const obj = {
					current: {},
					previous: {},
				};
				headers.forEach((column, inx) => {
					if (row1[inx] !== null) {
						obj.current[column] = lookups[column]?.[rowInx] ?? row1[inx];
					}
					if (row2[inx] !== null) {
						obj.previous[column] = lookups[column]?.[rowInx] ?? row2[inx];
					}
				});

				acc.push(obj);
				return acc;
			},
			[]
  );
};

const initialState = {
  dei: {
    isLoading: true,
    added: {
      male: [],
      female: [],
      unknown: [],
    },
    replied: {
      male: [],
      female: [],
      unknown: [],
    },
    interested: {
      male: [],
      female: [],
      unknown: [],
    },
    xAxis: [],
  },

  recruiterActivity: {
    isLoading: true,
    data: [],
  },
  flowActivity: {
    isLoading: true,
    data: [],
  },
  activityDrillDown: {
    isLoading: true,
    data: [],
  },

  repliedCandidatesByRecruiter: {
    isLoading: true,
    data: [],
  },
  repliedCandidatesByRecruiterDrillDown: {
    isLoading: true,
    data: [],
  },

  flowPerformance: {
    isLoading: true,
    data: [],
  },
  flowPerformanceDrillDown: {
    isLoading: true,
    data: [],
  },

  hiresPerformance: {
    isLoading: true,
    data: [],
  },
  hiresPerformanceDrillDown: {
    isLoading: true,
    data: [],
  },

  offerPerformance: {
    isLoading: true,
    data: [],
  },
  offerPerformanceDrillDown: {
    isLoading: true,
    data: [],
  },
};

const { reducer, actions } = createSlice({
  name: 'team-productivity',
  initialState,

  reducers: {
    clearActivityDrillDown: (state) => {
      state.activityDrillDown = initialState.activityDrillDown;
    },
    clearFlowPerformanceDrillDown: (state) => {
      state.flowPerformanceDrillDown = initialState.flowPerformanceDrillDown;
    },
    clearRepliedCandidatesByRecruiterDrillDown: (state) => {
      state.repliedCandidatesByRecruiterDrillDown =
        initialState.repliedCandidatesByRecruiterDrillDown;
    },
    clearHiresPerformanceDrillDown: (state) => {
      state.hiresPerformanceDrillDown = initialState.hiresPerformanceDrillDown;
    },
    clearOfferPerformanceDrillDown: (state) => {
      state.offerPerformanceDrillDown = initialState.offerPerformanceDrillDown;
    },
  },

  extraReducers: {
    [fetchDei.pending]: (state) => {
      state.dei.isLoading = true;
    },
    [fetchDei.fulfilled]: (state, { payload = {} }) => {
      state.dei = {
        isLoading: false,
        ...formatDeiData(payload),
      };
    },

    [fetchRecruiterActivity.pending]: (state) => {
      state.recruiterActivity.isLoading = true;
    },
    [fetchRecruiterActivity.fulfilled]: (state, { payload = {} }) => {
      state.recruiterActivity = {
        isLoading: false,
        data: constructTabularData(payload),
      };
    },
    [fetchFlowActivity.pending]: (state) => {
      state.flowActivity.isLoading = true;
    },
    [fetchFlowActivity.fulfilled]: (state, { payload = {} }) => {
      state.flowActivity = {
        isLoading: false,
        data: constructTabularData(payload),
      };
    },
    [fetchActivityDrillDown.pending]: (state) => {
      state.activityDrillDown.isLoading = true;
    },
    [fetchActivityDrillDown.fulfilled]: (state, { payload = {} }) => {
      state.activityDrillDown = {
        isLoading: false,
        data: constructTabularData(payload)
          .map((datum) => {
            return {
              contact: datum?.k1.contact,
              activity: datum?.k2,
            };
          })
          .filter(({ contact }) => !!contact),
      };
    },

    [fetchRepliedCandidatesByRecruiter.pending]: (state) => {
      state.repliedCandidatesByRecruiter.isLoading = true;
    },
    [fetchRepliedCandidatesByRecruiter.fulfilled]: (
      state,
      { payload = {} }
    ) => {
      state.repliedCandidatesByRecruiter = {
        isLoading: false,
        data: constructComparativeTabularData(payload),
      };
    },
    [fetchRepliedCandidatesByRecruiterDrillDown.pending]: (state) => {
      state.repliedCandidatesByRecruiterDrillDown.isLoading = true;
    },
    [fetchRepliedCandidatesByRecruiterDrillDown.fulfilled]: (
      state,
      { payload = {} }
    ) => {
      state.repliedCandidatesByRecruiterDrillDown = {
        isLoading: false,
        data: constructTabularData(payload)
          .map((datum) => {
            return {
              contact: datum?.k1.contact,
              activity: datum?.k2,
            };
          })
          .filter(({ contact }) => !!contact),
      };
    },

    [fetchFlowPerformance.pending]: (state) => {
      state.flowPerformance.isLoading = true;
    },
    [fetchFlowPerformance.fulfilled]: (state, { payload = {} }) => {
      state.flowPerformance = {
        isLoading: false,
        data: constructTabularData(payload).map((row) => ({
          ...row,
          interestedPercentage:
            row.contacted !== 0
              ? Math.floor((row.interested / row.contacted) * 100)
              : 0,
        })),
      };
    },
    [fetchFlowPerformanceDrillDown.pending]: (state) => {
      state.flowPerformanceDrillDown.isLoading = true;
    },
    [fetchFlowPerformanceDrillDown.fulfilled]: (state, { payload = {} }) => {
      state.flowPerformanceDrillDown = {
        isLoading: false,
        data: constructTabularData(payload)
          .map((datum) => {
            return {
              contact: datum?.k1.contact,
              activity: datum?.k2,
            };
          })
          .filter(({ contact }) => !!contact),
      };
    },

    [fetchAtsHiresPerformance.pending]: (state) => {
      state.hiresPerformance.isLoading = true;
    },
    [fetchAtsHiresPerformance.fulfilled]: (state, { payload = {} }) => {
      state.hiresPerformance = {
        isLoading: false,
        data: constructComparativeTabularData(payload),
      };
    },
    [fetchAtsHiresPerformanceDrillDown.pending]: (state) => {
      state.hiresPerformanceDrillDown.isLoading = true;
    },
    [fetchAtsHiresPerformanceDrillDown.fulfilled]: (
      state,
      { payload = {} }
    ) => {
      state.hiresPerformanceDrillDown = {
        isLoading: false,
        data: constructTabularData(payload).map((datum) => {
          const { ats_candidate, contact } = datum?.k1 ?? {};
          return formatAtsCandidate(ats_candidate ?? {}, contact ?? {});
        }),
      };
    },

    [fetchAtsOfferPerformance.pending]: (state) => {
      state.offerPerformance.isLoading = true;
    },
    [fetchAtsOfferPerformance.fulfilled]: (state, { payload = {} }) => {
      state.offerPerformance = {
        isLoading: false,
        data: constructComparativeTabularData(payload),
      };
    },
    [fetchAtsOfferPerformanceDrillDown.pending]: (state) => {
      state.offerPerformanceDrillDown.isLoading = true;
    },
    [fetchAtsOfferPerformanceDrillDown.fulfilled]: (
      state,
      { payload = {} }
    ) => {
      state.offerPerformanceDrillDown = {
        isLoading: false,
        data: constructTabularData(payload).map((datum) => {
          const { ats_candidate, contact } = datum?.k1 ?? {};
          return formatAtsCandidate(ats_candidate ?? {}, contact ?? {});
        }),
      };
    },
  },
});

export const {
  setTimeFrame,
  setQuickTimeFrame,
  clearActivityDrillDown,
  clearFlowPerformanceDrillDown,
  clearRepliedCandidatesByRecruiterDrillDown,
  clearHiresPerformanceDrillDown,
  clearOfferPerformanceDrillDown,
} = actions;
export default reducer;
